'use client'

import React from 'react'

import Image from 'next/image'
import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Spinner } from '@nextui-org/react'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'

import ICONS from '@/assets/icons'
import { useFollowUserMutation, useUnFollowUserMutation } from '@/redux/api/profile/profileW3'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { tempActions } from '@/redux/reducers/temp/temp.reducer'
import { useFollowSpaceMutation, useUnFollowSpaceMutation } from '@/redux/api/spaces'

type propsFeedProps = {
  creatorId?: string
  isFollowed?: boolean | null
  onUpdate?: () => void
  postId: string
  onDelete?: () => void
  postAs?: 'USER' | 'SPACE'
}

const BtnTool: React.FC<propsFeedProps> = ({ isFollowed, creatorId, onUpdate, onDelete, postAs }) => {
  const [unFollowUser, { isLoading: unFollowLoading }] = useUnFollowUserMutation()
  const [followUser, { isLoading: followLoading }] = useFollowUserMutation()
  const [followSpace, { isLoading }] = useFollowSpaceMutation()
  const [unFollowSpace, { isLoading: unFollowSpaceLoading }] = useUnFollowSpaceMutation()

  const dispatch = useAppDispatch()
  const listFollowed = useAppSelector((state) => state.temp.checkUserFollow)
  const user = useAppSelector((state) => state.root.auth.user)
  const userId = user?.id
  const spaceActive = useAppSelector((state) => state.root.settings.spaceActive)
  const _isFollowed = () => {
    if (!creatorId) return false

    const findUser = listFollowed.find((it) => it.id === creatorId)
    if (findUser) {
      return findUser.isFollowed
    }
    return isFollowed
  }
  const handleFollow = async () => {
    if (_isFollowed() && !creatorId) return
    try {
      let res
      if (postAs === 'USER') {
        res = await followUser({ userId: creatorId || '' }).unwrap()
      } else {
        res = await followSpace(creatorId || '').unwrap()
      }
      if (res.error) throw new Error('Follow failed')

      if (res) {
        toast.success('Follow success')
        dispatch(
          tempActions.setCheckUserFollow({
            id: creatorId,
            isFollowed: true,
          }),
        )
      }
    } catch (error) {
      toast.error('Follow failed')
    }
  }

  const handleUnFollow = async () => {
    if (!_isFollowed() && !creatorId) return
    try {
      let res
      if (postAs === 'USER') {
        res = await unFollowUser({ userId: creatorId || '' }).unwrap()
      } else {
        res = await unFollowSpace(creatorId || '').unwrap()
      }

      if (res.error) throw new Error('Unfollowed failed')

      if (res) {
        toast.success('Unfollowed success')
        dispatch(
          tempActions.setCheckUserFollow({
            id: creatorId,
            isFollowed: false,
          }),
        )
      }
    } catch (error) {
      toast.error('Follow failed')
    }
  }

  const isCreator = () => {
    if (spaceActive) {
      return spaceActive === creatorId
    }
    return userId === creatorId
  }

  const checkFollowed = () => {
    const listSpace = user?.spaces
    if (!creatorId) return false
    if (spaceActive) return false
    if (listSpace?.map((it) => it.id).includes(creatorId)) return false
    return true
  }

  const handleDeletePost = () => {
    Swal.mixin({
      background: '#0A0F1A',
      color: '#fff',
      confirmButtonColor: '#432D8D',
    })
      .fire({
        title: "<span class='text-lg md:text-2xl'>Do you want to delete this post?</span>",
        html: "<span class='text-sm md:text-base text-ss-grayscale-500'>You won't be able to revert this!</span>",
        showCancelButton: true,
        confirmButtonText: 'Confirm',
        customClass: {
          htmlContainer: 'pt-0',
          confirmButton: 'py-2 px-4 bg-btn-gradient text-base',
          cancelButton: 'py-2 px-4 bg-ss-grayscale-900 text-base',
        },
      })
      .then((result) => {
        if (result.isConfirmed) {
          onDelete?.()
        }
      })
  }

  if (isCreator())
    return (
      <Dropdown className="border border-white/20 bg-black">
        <DropdownTrigger>
          <div className="flex h-[22px] w-[22px] cursor-pointer items-center justify-center  ">
            <Image alt="" src={ICONS.threeDotHorizontal} width={22} height={22} />
          </div>
        </DropdownTrigger>
        <DropdownMenu variant="faded" aria-label="Dropdown menu with icons">
          {/* <DropdownItem
      className="text-white hover:border-white/10 data-[hover=true]:bg-ss-dark data-[hover=true]:text-white"
      key="report"
      startContent={<Image src={ICONS.icFlag} alt="" width={16} height={16} />}
    >
      Report post
    </DropdownItem> */}

          <DropdownItem
            textValue=""
            onClick={onUpdate}
            className="text-white hover:border-white/10 data-[hover=true]:bg-ss-dark data-[hover=true]:text-white "
            key="editPost"
            startContent={<Image src={ICONS.edit} alt="" width={16} height={16} />}
          >
            <div className="flex items-center gap-2">Edit</div>
          </DropdownItem>
          <DropdownItem
            textValue=""
            onClick={handleDeletePost}
            className="text-white hover:border-white/10 data-[hover=true]:bg-ss-dark data-[hover=true]:text-white "
            key="deletePost"
            startContent={<Image src={ICONS.icTrashWhite} alt="" width={16} height={16} />}
          >
            <div className="flex items-center gap-2">Delete</div>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    )
  if (checkFollowed()) {
    return (
      <Dropdown className="border border-white/20 bg-black">
        <DropdownTrigger>
          <div className="flex h-[22px] w-[22px] cursor-pointer items-center justify-center  ">
            <Image alt="" src={ICONS.threeDotHorizontal} width={22} height={22} />
          </div>
        </DropdownTrigger>
        <DropdownMenu variant="faded" aria-label="Dropdown menu with icons">
          {_isFollowed() ? (
            <DropdownItem
              textValue=""
              onClick={handleUnFollow}
              className="text-white hover:border-white/10 data-[hover=true]:bg-ss-dark data-[hover=true]:text-white"
              key="unfollow"
              startContent={<Image src={ICONS.icUserX} alt="" width={16} height={16} />}
            >
              <div className="flex items-center gap-2">
                Unfollow
                {(unFollowSpaceLoading || unFollowLoading) && <Spinner size="sm" />}
              </div>
            </DropdownItem>
          ) : (
            <DropdownItem
              textValue=""
              onClick={handleFollow}
              className="text-white hover:border-white/10 data-[hover=true]:bg-ss-dark data-[hover=true]:text-white "
              key="follow"
              startContent={<Image src={ICONS.icUserX} alt="" width={16} height={16} />}
            >
              <div className="flex items-center gap-2">
                Follow
                {(isLoading || followLoading) && <Spinner size="sm" />}
              </div>
            </DropdownItem>
          )}
        </DropdownMenu>
      </Dropdown>
    )
  }
}

export default BtnTool
